import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { motion } from 'framer-motion';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Check, X, Star, Filter, Eye, MessageSquare, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const STEP_LABELS = {
  onboarding: 'Onboarding',
  profile: 'Profil',
  vision: 'Vision',
  content: 'Contenu',
  first_action: 'Première Action'
};

const EMOTION_EMOJIS = {
  aligned: '✨',
  inspired: '🔥',
  confused: '🤔',
  motivated: '💎'
};

const AdminFeedback = () => {
  const [checkpoints, setCheckpoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStep, setFilterStep] = useState(null);
  const [filterApproved, setFilterApproved] = useState(null);
  const [total, setTotal] = useState(0);
  
  const { token, user } = useAuth();

  const fetchCheckpoints = async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/emotion-checkpoints?limit=100`;
      if (filterStep) url += `&step=${filterStep}`;
      if (filterApproved !== null) url += `&approved=${filterApproved}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCheckpoints(response.data.checkpoints);
      setTotal(response.data.total);
    } catch (err) {
      console.error('Error fetching checkpoints:', err);
      toast.error('Erreur lors du chargement des feedbacks');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchCheckpoints();
  }, [filterStep, filterApproved]);

  const handleApprove = async (checkpointId) => {
    try {
      await axios.put(
        `${API}/admin/emotion-checkpoints/${checkpointId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Feedback approuvé');
      fetchCheckpoints();
    } catch (err) {
      toast.error('Erreur lors de l\'approbation');
    }
  };

  const handleReject = async (checkpointId) => {
    try {
      await axios.put(
        `${API}/admin/emotion-checkpoints/${checkpointId}/reject`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Feedback rejeté');
      fetchCheckpoints();
    } catch (err) {
      toast.error('Erreur lors du rejet');
    }
  };

  const handleFeature = async (checkpointId, featured) => {
    try {
      await axios.put(
        `${API}/admin/emotion-checkpoints/${checkpointId}/feature`,
        { featured },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(featured ? 'Feedback mis en avant' : 'Feedback retiré de la mise en avant');
      fetchCheckpoints();
    } catch (err) {
      toast.error('Erreur');
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-px bg-gradient-to-r from-[#D4AF37] to-transparent" />
            <span className="font-accent text-[#D4AF37] text-[10px] tracking-[0.25em]">
              ADMIN
            </span>
          </div>
          
          <h1 
            className="text-white text-3xl md:text-4xl mb-2"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Gestion des Feedbacks
          </h1>
          
          <p className="text-[#71717A]">
            Approuvez, rejetez ou mettez en avant les témoignages utilisateurs.
          </p>
        </motion.div>

        <div className="bg-[#0A0A0A] border border-[#1C1C1C] p-4 mb-6">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Filter size={14} className="text-[#52525B]" />
              <span className="text-[#52525B] text-xs">Filtrer:</span>
            </div>
            
            <select
              value={filterStep || ''}
              onChange={(e) => setFilterStep(e.target.value || null)}
              className="bg-[#1C1C1C] border border-[#27272A] text-white text-sm px-3 py-2 outline-none"
              data-testid="filter-step"
            >
              <option value="">Toutes les étapes</option>
              {Object.entries(STEP_LABELS).map(([step, label]) => (
                <option key={step} value={step}>{label}</option>
              ))}
            </select>
            
            <select
              value={filterApproved === null ? '' : filterApproved.toString()}
              onChange={(e) => setFilterApproved(e.target.value === '' ? null : e.target.value === 'true')}
              className="bg-[#1C1C1C] border border-[#27272A] text-white text-sm px-3 py-2 outline-none"
              data-testid="filter-approved"
            >
              <option value="">Tous les statuts</option>
              <option value="true">Approuvés</option>
              <option value="false">En attente</option>
            </select>
            
            <div className="ml-auto flex items-center gap-2 text-[#71717A] text-sm">
              <Users size={14} />
              <span>{total} feedbacks</span>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="grid gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-[#0A0A0A] border border-[#1C1C1C] p-6 h-32 animate-pulse" />
            ))}
          </div>
        ) : checkpoints.length === 0 ? (
          <div className="text-center py-16">
            <MessageSquare className="mx-auto mb-4 text-[#52525B]" size={32} />
            <p className="text-[#71717A]">Aucun feedback pour le moment</p>
          </div>
        ) : (
          <div className="space-y-4">
            {checkpoints.map((checkpoint) => (
              <motion.div
                key={checkpoint.checkpoint_id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`bg-[#0A0A0A] border p-6 ${
                  checkpoint.approved_as_review
                    ? 'border-green-500/30'
                    : checkpoint.consent_testimonial
                    ? 'border-[#D4AF37]/30'
                    : 'border-[#1C1C1C]'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full flex items-center justify-center">
                        <span className="text-[#D4AF37] text-sm font-medium">
                          {checkpoint.user_name?.charAt(0)?.toUpperCase() || '?'}
                        </span>
                      </div>
                      <div>
                        <p className="text-white text-sm font-medium">
                          {checkpoint.user_name || 'Anonyme'}
                        </p>
                        <p className="text-[#52525B] text-xs">
                          {checkpoint.user_email}
                        </p>
                      </div>
                      
                      <div className="ml-auto flex items-center gap-2">
                        <span className="text-[#71717A] text-xs px-2 py-1 bg-[#1C1C1C]">
                          {STEP_LABELS[checkpoint.step]}
                        </span>
                        
                        {checkpoint.emotion_tag && (
                          <span className="text-lg">
                            {EMOTION_EMOJIS[checkpoint.emotion_tag]}
                          </span>
                        )}
                        
                        {checkpoint.featured && (
                          <Star className="text-[#D4AF37]" size={16} fill="#D4AF37" />
                        )}
                      </div>
                    </div>
                    
                    {checkpoint.feedback_text && (
                      <p className="text-[#A1A1AA] text-sm leading-relaxed pl-13 mb-3">
                        "{checkpoint.feedback_text}"
                      </p>
                    )}
                    
                    <div className="flex items-center gap-4 text-xs text-[#52525B]">
                      <span>
                        {new Date(checkpoint.created_at).toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                      
                      {checkpoint.consent_testimonial && (
                        <span className="text-green-500 flex items-center gap-1">
                          <Check size={12} />
                          Consentement témoignage
                        </span>
                      )}
                      
                      {checkpoint.approved_as_review && (
                        <span className="text-green-500 flex items-center gap-1">
                          <Eye size={12} />
                          Approuvé
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex flex-col gap-2">
                    {!checkpoint.approved_as_review && checkpoint.consent_testimonial && (
                      <Button
                        onClick={() => handleApprove(checkpoint.checkpoint_id)}
                        size="sm"
                        className="bg-green-500/20 text-green-400 hover:bg-green-500/30 border border-green-500/30"
                        data-testid={`approve-${checkpoint.checkpoint_id}`}
                      >
                        <Check size={14} className="mr-1" />
                        Approuver
                      </Button>
                    )}
                    
                    {checkpoint.approved_as_review && (
                      <>
                        <Button
                          onClick={() => handleFeature(checkpoint.checkpoint_id, !checkpoint.featured)}
                          size="sm"
                          className={`${
                            checkpoint.featured
                              ? 'bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/30'
                              : 'bg-[#1C1C1C] text-[#71717A] border-[#27272A]'
                          } border`}
                          data-testid={`feature-${checkpoint.checkpoint_id}`}
                        >
                          <Star size={14} className="mr-1" fill={checkpoint.featured ? "#D4AF37" : "none"} />
                          {checkpoint.featured ? 'Featured' : 'Mettre en avant'}
                        </Button>
                        
                        <Button
                          onClick={() => handleReject(checkpoint.checkpoint_id)}
                          size="sm"
                          className="bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30"
                          data-testid={`reject-${checkpoint.checkpoint_id}`}
                        >
                          <X size={14} className="mr-1" />
                          Retirer
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AdminFeedback;
